import type { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import type { PaletteItem } from './types';

const useStyles = makeStyles({
  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    height: 40,
    padding: '0 12px',
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    // The palette is keyboard-first: selection is one highlighted row, and a
    // browser focus ring on top of it would read as two selections.
    '&:focus': { outline: 'none' },
  },
  selected: {
    background: 'var(--hone-portal-fill-selected)',
    // Same active grammar as the sidebar's nav rows: a yellow bar, not a fill.
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 10,
      bottom: 10,
      width: 3,
      borderRadius: 2,
      background: 'var(--helix-colors-brand-500)',
    },
  },
  icon: {
    flex: 'none',
    width: 15,
    height: 15,
    opacity: 0.6,
    display: 'block',
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 13.5,
    color: 'var(--bui-fg-primary)',
  },
  /*
   * Entity names are identifiers, so they are set in mono here as they are in
   * the catalog cards and Recently viewed. 12.5 rather than 13.5 because DM
   * Mono runs larger on the body than DM Sans at a matched size — this is the
   * same pairing `RecentlyViewedPanel` uses.
   */
  identifier: {
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 12.5,
  },
  match: {
    fontWeight: 700,
    // Half-strength brand yellow: legible under ink without becoming a block.
    background: 'rgba(248, 249, 63, 0.5)',
    borderRadius: 2,
    color: 'inherit',
  },
  spacer: { flex: 1 },
  meta: {
    flex: 'none',
    fontSize: 11.5,
    color: 'var(--bui-fg-secondary)',
    whiteSpace: 'nowrap',
  },
  enter: {
    flex: 'none',
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 10,
    lineHeight: '14px',
    color: 'var(--bui-fg-secondary)',
    background: 'var(--hone-portal-fill-inset)',
    border: '1px solid var(--bui-border-1)',
    borderRadius: 4,
    padding: '0 5px',
  },
});

/**
 * Marks the first case-insensitive occurrence of the query inside the title.
 * Only the first: the index matches on prefixes, so a second hit is usually a
 * coincidence and lighting them all up reads as noise.
 */
function highlight(title: string, term: string, className: string): ReactNode {
  if (!term) {
    return title;
  }
  const at = title.toLowerCase().indexOf(term.toLowerCase());
  if (at === -1) {
    return title;
  }
  return (
    <>
      {title.slice(0, at)}
      <mark className={className}>{title.slice(at, at + term.length)}</mark>
      {title.slice(at + term.length)}
    </>
  );
}

export type SearchPaletteRowProps = {
  item: PaletteItem;
  term: string;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
};

export function SearchPaletteRow({
  item,
  term,
  isSelected,
  onSelect,
  onHover,
}: SearchPaletteRowProps) {
  const classes = useStyles();
  const Icon = item.icon;

  return (
    <button
      type="button"
      id={item.id}
      role="option"
      aria-selected={isSelected}
      tabIndex={-1}
      className={`${classes.row} ${isSelected ? classes.selected : ''}`}
      onClick={onSelect}
      onMouseMove={onHover}
    >
      <Icon className={classes.icon} />
      <span
        className={`${classes.title} ${
          item.isIdentifier ? classes.identifier : ''
        }`}
      >
        {highlight(item.title, term, classes.match)}
      </span>
      <span className={classes.spacer} />
      <span className={classes.meta}>{item.meta}</span>
      {isSelected && <span className={classes.enter}>↵</span>}
    </button>
  );
}
